import { __awaiter } from 'tslib';
import { InjectionToken, inject, PLATFORM_ID, APP_INITIALIZER, isDevMode, Injectable, Inject, ɵɵdefineInjectable, ɵɵinject, APP_BOOTSTRAP_LISTENER, Directive, Input, Optional, HostListener, Host, NgModule } from '@angular/core';
import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Provide a Injection Token to global settings.
 * @type {?}
 */
const NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN = new InjectionToken('ngx-google-analytics-settings', {
    factory: (/**
     * @return {?}
     */
    () => ({ trackingCode: '', ennableTracing: false }))
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Check if this environment can access Window object and return window or null if false.
 * @param {?} platformId
 * @return {?}
 */
function getWindow(platformId) {
    return (isPlatformBrowser(platformId))
        ? window : null;
}
/**
 * Provide DOM Window reference or null if the environment is not a Browser.
 * @type {?}
 */
const NGX_WINDOW = new InjectionToken('ngx-window', {
    providedIn: 'root',
    factory: (/**
     * @return {?}
     */
    () => getWindow(inject(PLATFORM_ID))),
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Check if there is some global function called gtag on Window object, or create an empty function to doesn't brake codes...
 * @param {?} window
 * @return {?}
 */
function getDataLayerFn(window) {
    return (window)
        ? window['dataLayer'] = window['dataLayer'] || []
        : null;
}
/**
 * Provides an injection token to access Google Analytics DataLayer Collection
 * @type {?}
 */
const NGX_DATA_LAYER = new InjectionToken('ngx-data-layer', {
    providedIn: 'root',
    factory: (/**
     * @return {?}
     */
    () => getDataLayerFn(inject(NGX_WINDOW)))
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Check if there is some global function called gtag on Window object, or create an empty function to doesn't brake codes...
 * @param {?} window
 * @param {?} dataLayer
 * @return {?}
 */
function getGtagFn(window, dataLayer) {
    return (window)
        ? window['gtag'] = window['gtag'] || (/**
         * @return {?}
         */
        function () {
            dataLayer.push((/** @type {?} */ (arguments)));
        })
        : null;
}
/**
 * Provides an injection token to access Google Analytics Gtag Function
 * @type {?}
 */
const NGX_GTAG_FN = new InjectionToken('ngx-gtag-fn', {
    providedIn: 'root',
    factory: (/**
     * @return {?}
     */
    () => getGtagFn(inject(NGX_WINDOW), inject(NGX_DATA_LAYER)))
});

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Provide a DI Configuration to attach GA Initialization at Angular Startup Cycle.
 * @type {?}
 */
const NGX_GOOGLE_ANALYTICS_INITIALIZER_PROVIDER = {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: GoogleAnalyticsInitializer,
    deps: [
        NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,
        NGX_GTAG_FN,
        DOCUMENT
    ]
};
/**
 * Create a script element on DOM and link it to Google Analytics tracking code URI.
 * After that, execute exactly same init process as tracking snippet code.
 * @param {?} settings
 * @param {?} gtag
 * @param {?} document
 * @return {?}
 */
function GoogleAnalyticsInitializer(settings, gtag, document) {
    return (/**
     * @return {?}
     */
    () => __awaiter(this, void 0, void 0, function* () {
        if (!settings.trackingCode) {
            if (!isDevMode()) {
                console.error('Empty tracking code for Google Analytics. Make sure to provide one when initializing NgxGoogleAnalyticsModule.');
            }
            return;
        }
        if (!gtag) {
            if (!isDevMode()) {
                console.error('Was not possible create or read gtag() fn. Make sure this module is running on a Browser w/ access to Window interface.');
            }
            return;
        }
        if (!document) {
            if (!isDevMode()) {
                console.error('Was not possible to access Document interface. Make sure this module is running on a Browser w/ access do Document interface.');
            }
        }
        // Set default ga.js uri
        settings.uri = settings.uri || `https://www.googletagmanager.com/gtag/js?id=${settings.trackingCode}`;
        // these commands should run first!
        /** @type {?} */
        const initialCommands = [
            { command: 'js', values: [new Date()] },
            { command: 'config', values: [settings.trackingCode] }
        ];
        settings.initCommands = [...initialCommands, ...(settings.initCommands || [])];
        for (const command of settings.initCommands) {
            gtag(command.command, ...command.values);
        }
        /** @type {?} */
        const s = document.createElement('script');
        s.async = true;
        s.src = settings.uri;
        /** @type {?} */
        const head = document.getElementsByTagName('head')[0];
        head.appendChild(s);
    }));
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class GoogleAnalyticsService {
    /**
     * @param {?} settings
     * @param {?} _document
     * @param {?} _gtag
     */
    constructor(settings, _document, _gtag) {
        this.settings = settings;
        this._document = _document;
        this._gtag = _gtag;
        this.document = _document;
    }
    /**
     * @private
     * @param {?} err
     * @return {?}
     */
    throw(err) {
        if ((this.settings.ennableTracing || isDevMode()) && console && console.error) {
            console.error(err);
        }
    }
    /**
     * \@todo Change this to `Object.fromEntity()` in the future...
     * @private
     * @param {?} map
     * @return {?}
     */
    toKeyValue(map) {
        return (map.size > 0)
            ? Array.from(map).reduce((/**
             * @param {?} obj
             * @param {?} __1
             * @return {?}
             */
            (obj, [key, value]) => Object.defineProperty(obj, key, { value, enumerable: true })), {})
            : undefined;
    }
    /**
     * Call native GA Tag
     *
     * @param {?} action config|event
     * @param {?} command Action specialization
     * @param {...?} options Command configuration
     * @return {?}
     */
    gtag(action, command, ...options) {
        try {
            this._gtag(action, command, ...options);
        }
        catch (err) {
            this.throw(err);
        }
    }
    /**
     * Send an event trigger to GA. It is the same as call:
     * ```js
     * gtag('event', 'video_auto_play_start', {
     *   'event_label': 'My promotional video',
     *   'event_category': 'video_auto_play'
     * });
     * ```
     *
     * @param {?} action 'video_auto_play_start'
     * @param {?=} category 'video_auto_play'
     * @param {?=} label 'My promotional video'
     * @param {?=} value An value to measure something
     * @param {?=} interaction
     * @return {?}
     */
    event(action, category, label, value, interaction) {
        try {
            /** @type {?} */
            const opt = new Map();
            if (category) {
                opt.set('event_category', category);
            }
            if (label) {
                opt.set('event_label', label);
            }
            if (value) {
                opt.set('value', value);
            }
            if (interaction !== undefined) {
                opt.set('interaction', interaction);
            }
            /** @type {?} */
            const params = this.toKeyValue(opt);
            if (params) {
                this.gtag('event', (/** @type {?} */ (action)), params);
            }
            else {
                this.gtag('event', (/** @type {?} */ (action)));
            }
        }
        catch (error) {
            this.throw(error);
        }
    }
    /**
     * Send an page view event. This is the same as
     *
     * ```js
     * gtag('config', 'GA_TRACKING_ID', {
     *   'page_title' : 'Homepage',
     *   'page_path': '/home'
     * });
     * ```
     *
     * The tracking ID is injected automatically by Inject Token NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN
     *
     * @param {?} path /home
     * @param {?=} title Homepage
     * @param {?=} location '{ page_location }'
     * @param {?=} options '{ ... custom dimentions }'
     * @return {?}
     */
    pageView(path, title, location, options) {
        try {
            /** @type {?} */
            const opt = new Map([['page_path', path]]);
            if (title) {
                opt.set('page_title', title);
            }
            if (location || this.document) {
                opt.set('page_location', (location || this.document.location.href));
            }
            if (options) {
                Object
                    .entries(options)
                    .map((/**
                 * @param {?} __0
                 * @return {?}
                 */
                ([key, value]) => opt.set(key, value)));
            }
            this.gtag('config', this.settings.trackingCode, this.toKeyValue(opt));
        }
        catch (error) {
            this.throw(error);
        }
    }
    /**
     * Send an event to report a App Page View. It is the same as
     *
     * ```js
     * gtag('event', 'screen_view', {
     *   'app_name': 'myAppName',
     *   'screen_name' : 'Home'
     * });
     *
     * ```
     *
     * @param {?} screen 'screen_name'
     * @param {?} appName
     * @param {?=} appId 'app_id'
     * @param {?=} appVersion 'app_version'
     * @param {?=} installerId 'app_installer_id'
     * @return {?}
     */
    appView(screen, appName, appId, appVersion, installerId) {
        try {
            /** @type {?} */
            const opt = new Map([['screen_name', screen], ['app_name', appName]]);
            if (appId) {
                opt.set('app_id', appId);
            }
            if (appVersion) {
                opt.set('app_version', appVersion);
            }
            if (installerId) {
                opt.set('app_installer_id', installerId);
            }
            this.gtag('event', 'screen_view', this.toKeyValue(opt));
        }
        catch (error) {
            this.throw(error);
        }
    }
    /**
     * Send an event to GA to report an application error. It is the same as
     *
     * ```js
     * gtag('event', 'exception', {
     *   'description': 'error_description',
     *   'fatal': false   // set to true if the error is fatal
     * });
     * ```
     *
     * @param {?=} description 'error_description'
     * @param {?=} fatal set to true if the error is fatal
     * @return {?}
     */
    exception(description, fatal) {
        try {
            /** @type {?} */
            const opt = new Map();
            if (description) {
                opt.set('description', description);
            }
            if (fatal) {
                opt.set('fatal', fatal);
            }
            /** @type {?} */
            const params = this.toKeyValue(opt);
            if (params) {
                this.gtag('event', 'exception', this.toKeyValue(opt));
            }
            else {
                this.gtag('event', 'exception');
            }
        }
        catch (error) {
            this.throw(error);
        }
    }
}
GoogleAnalyticsService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root'
            },] }
];
/** @nocollapse */
GoogleAnalyticsService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [DOCUMENT,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [NGX_GTAG_FN,] }] }
];
/** @nocollapse */ GoogleAnalyticsService.ngInjectableDef = ɵɵdefineInjectable({ factory: function GoogleAnalyticsService_Factory() { return new GoogleAnalyticsService(ɵɵinject(NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN), ɵɵinject(DOCUMENT), ɵɵinject(NGX_GTAG_FN)); }, token: GoogleAnalyticsService, providedIn: "root" });

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Provide a DI Configuration to attach GA Trigger to Router Events at Angular Startup Cycle.
 * @type {?}
 */
const NGX_GOOGLE_ANALYTICS_ROUTER_INITIALIZER_PROVIDER = {
    provide: APP_BOOTSTRAP_LISTENER,
    multi: true,
    useFactory: GoogleAnalyticsRouterInitializer,
    deps: [
        GoogleAnalyticsService
    ]
};
/**
 * Attach a listener to `NavigationEnd` Router event. So, every time Router finish the page resolution it should call `NavigationEnd` event.
 * We assume that NavigationEnd is the final page resolution and call GA `page_view` command.
 *
 * To avoid double binds, we also destroy the subscription when de Bootstrap Component is destroied. But, we don't know for sure
 * that this strategy does not cause double bind on multiple bootstrap components.
 *
 * We are using de component's injector reference to resolve Router, sou I hope there is no problem w/ double bing.
 *
 * If you have this problem, I encourage not Use NgxGoogleAnalyticsRouterModule and atach the listener on AppComponent initialization.
 * @param {?} gaService
 * @return {?}
 */
function GoogleAnalyticsRouterInitializer(gaService) {
    return (/**
     * @param {?} c
     * @return {?}
     */
    (c) => __awaiter(this, void 0, void 0, function* () {
        /** @type {?} */
        const router = c.injector.get(Router);
        /** @type {?} */
        const subs = router
            .events
            .subscribe((/**
         * @param {?} event
         * @return {?}
         */
        event => {
            if (event instanceof NavigationEnd) {
                gaService.pageView(event.urlAfterRedirects, undefined);
            }
        }));
        // Cleanup
        c.onDestroy((/**
         * @return {?}
         */
        () => subs.unsubscribe()));
    }));
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class GaEventCategoryDirective {
    constructor() { }
}
GaEventCategoryDirective.decorators = [
    { type: Directive, args: [{
                selector: `[gaEvent][gaCategory],
             [gaCategory]`,
                exportAs: 'gaCategory'
            },] }
];
/** @nocollapse */
GaEventCategoryDirective.ctorParameters = () => [];
GaEventCategoryDirective.propDecorators = {
    gaCategory: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class GaEventDirective {
    /**
     * @param {?} gaCategoryDirective
     * @param {?} gaService
     * @param {?} settings
     */
    constructor(gaCategoryDirective, gaService, settings) {
        this.gaCategoryDirective = gaCategoryDirective;
        this.gaService = gaService;
        this.settings = settings;
        this.gaBind = 'click';
    }
    /**
     * @return {?}
     */
    ngOnInit() {
    }
    /**
     * @return {?}
     */
    onClick() {
        if (this.gaBind === 'click') {
            this.trigger();
        }
    }
    /**
     * @return {?}
     */
    onFocus() {
        if (this.gaBind === 'focus') {
            this.trigger();
        }
    }
    /**
     * @return {?}
     */
    onBlur() {
        if (this.gaBind === 'blur') {
            this.trigger();
        }
    }
    /**
     * @protected
     * @return {?}
     */
    trigger() {
        try {
            // Observação: não é obrigatório especificar uma categoria, uma etiqueta ou um valor. Consulte Eventos padrão do Google Analytics abaixo.
            // if (!this.$gaCategoryDirective) {
            //   throw new Error('You must provide a gaCategory attribute w/ gaEvent Directive.');
            // }
            if (!this.gaAction && !this.gaEvent) {
                throw new Error('You must provide a gaAction atrribute to identify this event.');
            }
            this.gaService
                .event(this.gaAction || this.gaEvent, (this.gaCategoryDirective) ? this.gaCategoryDirective.gaCategory : undefined, this.gaLabel || this.label, this.gaValue, this.gaInteraction);
        }
        catch (err) {
            this.throw(err);
        }
    }
    /**
     * @protected
     * @param {?} err
     * @return {?}
     */
    throw(err) {
        if ((isDevMode() || this.settings.ennableTracing) && console && console.warn) {
            console.warn(err);
        }
    }
}
GaEventDirective.decorators = [
    { type: Directive, args: [{
                selector: `[gaEvent]`,
                exportAs: 'gaEvent'
            },] }
];
/** @nocollapse */
GaEventDirective.ctorParameters = () => [
    { type: GaEventCategoryDirective, decorators: [{ type: Optional }] },
    { type: GoogleAnalyticsService },
    { type: undefined, decorators: [{ type: Inject, args: [NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,] }] }
];
GaEventDirective.propDecorators = {
    gaAction: [{ type: Input }],
    gaLabel: [{ type: Input }],
    label: [{ type: Input }],
    gaValue: [{ type: Input }],
    gaInteraction: [{ type: Input }],
    gaBind: [{ type: Input }],
    gaEvent: [{ type: Input }],
    onClick: [{ type: HostListener, args: ['click',] }],
    onFocus: [{ type: HostListener, args: ['focus',] }],
    onBlur: [{ type: HostListener, args: ['blur',] }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class GaEventFormInputDirective {
    /**
     * @param {?} gaEvent
     */
    constructor(gaEvent) {
        this.gaEvent = gaEvent;
        this.gaBind = 'focus';
    }
    /**
     * @param {?} bind
     * @return {?}
     */
    set gaBind(bind) {
        if (this.gaEvent) {
            this.gaEvent.gaBind = bind;
        }
    }
    /**
     * @return {?}
     */
    get gaBind() {
        return (this.gaEvent)
            ? this.gaEvent.gaBind
            : null;
    }
}
GaEventFormInputDirective.decorators = [
    { type: Directive, args: [{
                selector: `input[gaEvent],
             select[gaEvent],
             textarea[gaEvent]`
            },] }
];
/** @nocollapse */
GaEventFormInputDirective.ctorParameters = () => [
    { type: GaEventDirective, decorators: [{ type: Host }, { type: Optional }] }
];
GaEventFormInputDirective.propDecorators = {
    gaBind: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Install Google Analytics Tracking code on your environment and configure tracking ID.
 *
 * This module should be a dependency on the higest level module of the applicadion, ie AppModule on most use cases.
 */
class NgxGoogleAnalyticsModule {
    /**
     * You should provide a valid Google TrackingCode. This code will be provided to the entire application by
     * `NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN` token. You can inject this code in you componentes if you like by
     * use the following injection code `\@Inject(NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN) gaCongig: IGoogleAnalyticsSettings`
     *
     * @param {?} trackingCode The Google Tracking Code
     * @param {?=} commands When placed, it will run any GA Commangs in sequence after setup GA environment.
     * @param {?=} uri When placed, it will change the default js URI to the provided one.
     * @param {?=} enableTracing
     * @return {?}
     */
    static forRoot(trackingCode, commands = [], uri, enableTracing) {
        return {
            ngModule: NgxGoogleAnalyticsModule,
            providers: [
                {
                    provide: NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN,
                    useValue: {
                        trackingCode,
                        commands,
                        uri,
                        enableTracing
                    }
                },
                NGX_GOOGLE_ANALYTICS_INITIALIZER_PROVIDER,
            ]
        };
    }
}
NgxGoogleAnalyticsModule.decorators = [
    { type: NgModule, args: [{
                imports: [],
                declarations: [
                    GaEventDirective,
                    GaEventCategoryDirective,
                    GaEventFormInputDirective
                ],
                exports: [
                    GaEventDirective,
                    GaEventCategoryDirective,
                    GaEventFormInputDirective
                ]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * Attach a listener to `NavigationEnd` Router event. So, every time Router finish the page resolution it should call `NavigationEnd` event.
 * We assume that NavigationEnd is the final page resolution and call GA `page_view` command.
 *
 * To avoid double binds, we also destroy the subscription when de Bootstrap Component is destroied. But, we don't know for sure
 * that this strategy does not cause double bind on multiple bootstrap components.
 *
 * We are using de component's injector reference to resolve Router, sou I hope there is no problem w/ double bing.
 *
 * If you have this problem, I encourage not Use NgxGoogleAnalyticsRouterModule and atach the listener on AppComponent initialization.
 *
 * This Module is just a sugar for:
 *
 * ```typescript
 * constructor(private router: Router) {}
 * ...
 * ngOnInit() {
 * ...
 * this.router
 * .events
 * .pipe(takeUntil(this.onDestroy$))
 * .subscribe(event => {
 * if (event instanceof NavigationEnd) {
 * gaService.pageView(event.urlAfterRedirects, undefined);
 * }
 * });
 * ```
 */
class NgxGoogleAnalyticsRouterModule {
}
NgxGoogleAnalyticsRouterModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule,
                    NgxGoogleAnalyticsModule
                ],
                providers: [
                    NGX_GOOGLE_ANALYTICS_ROUTER_INITIALIZER_PROVIDER
                ],
                declarations: []
            },] }
];

export { GaEventCategoryDirective, GaEventDirective, GaEventFormInputDirective, GoogleAnalyticsInitializer, GoogleAnalyticsRouterInitializer, GoogleAnalyticsService, NGX_DATA_LAYER, NGX_GOOGLE_ANALYTICS_INITIALIZER_PROVIDER, NGX_GOOGLE_ANALYTICS_ROUTER_INITIALIZER_PROVIDER, NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN, NGX_GTAG_FN, NGX_WINDOW, NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule, getDataLayerFn, getGtagFn, getWindow };
//# sourceMappingURL=ngx-google-analytics.js.map
